import { useState } from 'react';
import { useParams } from 'react-router-dom';
import Button from 'form5/react/Button';

import { Callout } from '…/app/common/Callout/Callout.tsx';
import { SubmitButton } from '…/app/common/SubmitButton/SubmitButton.jsx';
import { Dialog } from '…/app/w/workspace/common/Dialog/Dialog.tsx';

import { deleteIntegrationCredentials } from './queries/deleteIntegrationCredentials.op.mts';
import type { Integration } from './IntegrationsSection.tsx';

interface DeleteIntegrationDialogProps {
  open: boolean;
  integration: Integration | null;
  setRefresh: (refresh: boolean) => void;
  setOpen: (open: Integration | null) => void;
}

export function DeleteIntegrationDialog({
  open,
  integration,
  setRefresh,
  setOpen,
}: DeleteIntegrationDialogProps) {
  const { workspaceId } = useParams();

  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<Error | null>(null);

  if (!integration) return null;

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      await deleteIntegrationCredentials({
        integrationId: integration.id,
        workspaceId,
      });
      setRefresh(true);
    } catch (error) {
      setErrors(error instanceof Error ? error : new Error('Unknown error occurred'));
    } finally {
      setIsSaving(false);
      setOpen(null);
    }
  };

  return (
    <Dialog
      backdropped
      open={!!open}
      setOpen={() => setOpen(null)}
    >
      <p>
        Are you sure you want to delete the <strong>{integration?.accountType}</strong> integration?
      </p>

      <div>
        <Callout type='error'>
          After deleting the integration, you will lose the ability to import data from Hivebrite into your audiences.

          All configurations associated with this integration will be deleted.
        </Callout>
      </div>

      {errors && (
        <Callout type='error'>
          {errors.message}
        </Callout>
      )}

      <SubmitButton
        appearance={Button.APPEARANCES.DANGER}
        disabled={isSaving}
        isSubmitting={isSaving}
        onClick={handleDelete}
        type="submit"
      >
        Delete
      </SubmitButton>
    </Dialog>
  );
}

DeleteIntegrationDialog.displayName = 'DeleteIntegrationDialog';
